import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { Neutrals, Primary } from '../../../core-components/theme/color-palette/colors';
import { settingsRowSx } from '../../../core-components/theme/components/SettingsList';

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

export const GROUP_GAP = 6;

export const profileColumnSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: GROUP_GAP };

export const blockActionSx: SystemStyleObject<Theme> = {
  inlineSize: { xs: '100%', md: 'auto' },
  alignSelf: { xs: 'stretch', md: 'flex-start' },
};

export const contactRowSx: SystemStyleObject<Theme> = {
  ...settingsRowSx,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  columnGap: 3,
  rowGap: 3,
  alignItems: 'start',
};

export const contactTextSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,
  minInlineSize: 0,
};

export const contactLabelSx: SystemStyleObject<Theme> = { color: Neutrals[600] };

export const contactValueSx: SystemStyleObject<Theme> = {
  color: Neutrals[900],
  fontVariantNumeric: 'tabular-nums',
  overflowWrap: 'anywhere',
  alignSelf: 'stretch',
};

export const contactDescriptionSx: SystemStyleObject<Theme> = { color: Neutrals[600] };

export const contactFollowUpSx: SystemStyleObject<Theme> = {
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minInlineSize: 0,
};

export const valueWithIconSx: SystemStyleObject<Theme> = { display: 'inline-flex', alignItems: 'center', gap: 1 };

export const rowFollowUpSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };

export const sendPairSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'stretch', md: 'flex-start' },
  gap: 1,
};

export const inlineActionSx: SystemStyleObject<Theme> = { alignSelf: 'flex-start' };

export const codeLeadSx: SystemStyleObject<Theme> = { color: Neutrals[700] };

const STATUS_LINE_HEIGHT = 1.5;

export const inlineStatusSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  color: Neutrals[700],
  minBlockSize: `calc(${theme.typography.label.fontSize} * ${STATUS_LINE_HEIGHT})`,
});

export const passwordCardSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: { xs: 0, md: 6 },
  borderColor: { xs: 'transparent', md: Neutrals[100] },
  backgroundColor: { xs: 'transparent', md: 'background.paper' },
  [theme.breakpoints.up('md')]: { boxSizing: 'border-box', maxInlineSize: theme.breakpoints.values.sm },
});

export const fieldWithHintSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };

export const fieldHintSx: SystemStyleObject<Theme> = { color: Neutrals[600] };

export const requirementSx: SystemStyleObject<Theme> = { display: 'inline-flex', alignItems: 'center', gap: 1 };

export const skeletonGroupSx =
  (rows: number) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    blockSize: `calc(${rows} * (${theme.controlHeight.medium}px + ${theme.spacing(6)}))`,
    borderRadius: 2.5,
    [REDUCED_MOTION]: { animation: 'none', '&::after': { animation: 'none' } },
  });

export const skeletonHeadingSx: SystemStyleObject<Theme> = {
  inlineSize: '40%',
  marginBlockEnd: 2,
  [REDUCED_MOTION]: { animation: 'none', '&::after': { animation: 'none' } },
};

const PICTURE_SIZE = { xs: 120, sm: 180 } as const;

export const pictureColumnSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'stretch', sm: 'flex-start' },
  gap: GROUP_GAP,
};

export const pictureRowSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'center', sm: 'flex-end' },
  gap: 4,
};

export const pictureAvatarSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...theme.typography.title,
  inlineSize: PICTURE_SIZE.xs,
  blockSize: PICTURE_SIZE.xs,
  color: theme.palette.common.white,
  backgroundColor: Primary[900],
  [theme.breakpoints.up('sm')]: { inlineSize: PICTURE_SIZE.sm, blockSize: PICTURE_SIZE.sm },
});
